import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { HtmlTooltip } from "../../../components/commonComponents/StyledComponents/styled";

// Fixed widths so every row's Impressions / Performance buttons line up.
// Sized for the longest status label of each type (Preparing / Add Products).
export const IMPRESSIONS_BUTTON_WIDTH = 132;
export const PERFORMANCE_BUTTON_WIDTH = 152;
export const REPORT_BUTTON_GAP = 8;
export const COMBINED_REPORTS_WIDTH = IMPRESSIONS_BUTTON_WIDTH + REPORT_BUTTON_GAP + PERFORMANCE_BUTTON_WIDTH;

// Wait before showing the state tooltip so a quick pass or click does not flash it.
const TOOLTIP_DELAY_MS = 400;

const VARIANT_STYLES = {
  // Preparing, Add Products — white fill, red outline
  outlined: {
    color: "#AF3241",
    backgroundColor: "#fff",
    border: "1px solid #AF3241",
    "&:hover": {
      backgroundColor: "#fff",
      border: "1px solid #AF3241",
    },
  },
  // Available
  solid: {
    color: "#fff",
    backgroundColor: "#AF3241",
    border: "1px solid #AF3241",
    "&:hover": {
      backgroundColor: "#AF3241",
    },
  },
  // Request
  dark: {
    color: "#fff",
    backgroundColor: "#2D2D37",
    border: "1px solid #2D2D37",
    "&:hover": {
      backgroundColor: "#2D2D37",
    },
  },
  // Requested (in progress, not clickable)
  grey: {
    color: "#fff",
    backgroundColor: "#62626E",
    border: "1px solid #62626E",
    "&:hover": {
      backgroundColor: "#62626E",
    },
  },
  // Full Report — both reports ready
  lightRed: {
    color: "#fff",
    backgroundColor: "#F04B55",
    border: "1px solid #F04B55",
    "&:hover": {
      backgroundColor: "#F04B55",
    },
  },
};

/**
 * Compact two-line report button used in the Contact-based Campaigns Reports column.
 * title sits on the first line (Impressions / Performance / Full Report),
 * status sits underneath in a smaller weight (Preparing, Available, …).
 */
const ReportStateButton = ({
  title,
  status,
  icon,
  variant,
  width,
  onClick,
  ariaText,
  tooltip,
  disabled = false,
}) => {
  const variantStyle = VARIANT_STYLES[variant] || VARIANT_STYLES.solid;

  const button = (
    <Button
      component="button"
      variant="contained"
      disableElevation
      disabled={disabled}
      startIcon={icon}
      aria-label={ariaText || `${title} ${status}`}
      onClick={(event) => {
        event.preventDefault();
        if (disabled !== true && typeof onClick === "function") {
          onClick(event);
        }
      }}
      sx={{
        width: width,
        minWidth: width,
        maxWidth: width,
        minHeight: 40,
        px: 1,
        py: 0.25,
        borderRadius: "8px",
        textTransform: "none",
        boxShadow: "none",
        cursor: disabled === true ? "not-allowed" : "pointer",
        "&.Mui-disabled": {
          opacity: 1,
          ...variantStyle,
        },
        "& .MuiButton-startIcon": {
          marginRight: "6px",
          marginLeft: 0,
          // MUI startIcon forces 20px on the first child — override that here
          "& > *:nth-of-type(1)": {
            fontSize: 16,
          },
        },
        ...variantStyle,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left", lineHeight: 1.15 }}>
        <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2, color: "inherit" }}>
          {title}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: 10,
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: 0.3,
            textTransform: "uppercase",
            color: variant === "outlined" ? "rgba(175, 50, 65, 0.7)" : "inherit",
          }}
        >
          {status}
        </Typography>
      </Box>
    </Button>
  );

  if (!tooltip) {
    return button;
  }

  return (
    <HtmlTooltip title={tooltip} enterDelay={TOOLTIP_DELAY_MS} enterNextDelay={TOOLTIP_DELAY_MS}>
      <span style={{ display: "inline-flex" }}>{button}</span>
    </HtmlTooltip>
  );
};

export default ReportStateButton;
